/**
 * Box 13: the top 5 campaigns by spend (spec §3.3), from get_campaign_summary.
 * The summary has no currency (R3): a campaign's currency is its account's,
 * as the facts rows give it, and a campaign of unknown currency is in no
 * currency. The summary COALESCEs every metric to 0, so a platform that does
 * not report the Result metric is null here, never that 0 (R4) — whether
 * coverage says so or the facts do (F4: coverage is null for all workspaces,
 * loading or failed, so the facts in hand decide too), or, for reach, the
 * campaign's own cut row (reachNotSent). Result reads the cut window, like
 * every Result (R2). Pure.
 *
 * @module services/overview/topCampaigns
 */
import type { CampaignSummaryRow } from '@/services/mediaIntelligenceShared';
import { classifyCampaignName } from '@/services/looker/campaignNaming';
import type { LookerCoverage } from '@/services/looker/lookerCoverage';
import { isChannelShown } from '@/services/channelFilter';
import { reachNotSent } from '@/services/platformReach';
import { metricFor, type AwarenessMetric, type ResultMetric } from '@/services/targets/resultMetric';
import type { TargetObjective } from '@/services/targets/targetRules';
import { asTargetObjective } from './objectiveResults';
import { nullableNumber } from './overviewMath';
import type { ObjectiveFilter, ResultState } from './overviewTypes';

export const TOP_CAMPAIGNS = 5;

export interface SummaryReads {
  current: CampaignSummaryRow[];
  /** The Result window's rows; [] when no day qualifies. */
  cut: CampaignSummaryRow[];
  /** campaign id → account id (null = none). */
  campaignAccount: Record<string, string | null>;
}

export interface CampaignRow {
  id: string;
  name: string;
  platform: string;
  objective: string;
  spend: number;
  /** null = not reported, no Result for this objective, no definition, or nothing spent in the cut window. */
  result: number | null;
  metric: ResultMetric | null;
  /** notReported = the platform does not report the metric; none = no cut window, or no spend in it (I2). */
  resultState: ResultState;
  unclassified: boolean;
}

export interface TopCampaignInput {
  summary: SummaryReads;
  /** account id → currency, from the facts rows. */
  accountCurrency: ReadonlyMap<string, string | null>;
  channels: readonly string[];
  objective: ObjectiveFilter;
  currency: string | null;
  awareness: AwarenessMetric | null;
  /** Result gaps when coverage is ready; null otherwise (all workspaces, loading, failed). */
  coverage: LookerCoverage | null;
  /** false = the Result window is empty (the whole period is inside the last 3 days). */
  hasCut: boolean;
  /**
   * F4: `unreportedKey(platform, objective)` pairs whose cut fact rows with spend > 0
   * all hold no value for the mapped Result metric (metricValueOf). OR-ed with coverage's gaps. Required (M2):
   * omitting it would fall back to the summary's COALESCEd 0 whenever coverage is null.
   */
  unreportedFromFacts: ReadonlySet<string>;
}

/** The key of one (platform, objective) pair in `unreportedFromFacts`. */
export const unreportedKey = (platform: string, objective: TargetObjective): string => `${platform}|${objective}`;

const SUMMARY_FIELD: Record<ResultMetric, keyof CampaignSummaryRow> = {
  impressions: 'total_impressions', reach: 'total_reach', clicks: 'total_clicks', conversions: 'total_conversions',
};

/** get_campaign_summary returns objective::text, which is NULL for a campaign with none. */
const objectiveOf = (r: CampaignSummaryRow): string => (r.objective as string | null) ?? 'unspecified';

const currencyOf = (id: string, input: TopCampaignInput): string | null => {
  const account = input.summary.campaignAccount[id];
  return account ? input.accountCurrency.get(account) ?? null : null;
};

type CampaignResult = Pick<CampaignRow, 'result' | 'metric' | 'resultState'>;

const resultOf = (r: CampaignSummaryRow, cutRow: CampaignSummaryRow | undefined, input: TopCampaignInput): CampaignResult => {
  const o = asTargetObjective(objectiveOf(r));
  if (o === null || input.awareness === null) return { result: null, metric: null, resultState: 'none' };
  const metric = metricFor(o, input.awareness);
  // I2: no cut window, or nothing spent in it → "—" (the summary's 0 there is a COALESCE, not a Result).
  if (!input.hasCut || !cutRow || !((nullableNumber(cutRow.total_spend) ?? 0) > 0)) return { result: null, metric, resultState: 'none' };
  const unreported = (input.coverage?.resultGaps[o].some((g) => g.platform === r.platform) ?? false)
    || input.unreportedFromFacts.has(unreportedKey(r.platform, o))
    // OQ1: the campaign's own cut reach, as D3 judges each creative.
    || (metric === 'reach' && reachNotSent(r.platform, nullableNumber(cutRow.total_reach), nullableNumber(cutRow.total_impressions)));
  if (unreported) return { result: null, metric, resultState: 'notReported' };
  const result = nullableNumber(cutRow[SUMMARY_FIELD[metric]] as number | null | undefined);
  return { result, metric, resultState: result === null ? 'none' : 'value' };
};

export const topCampaignsOf = (input: TopCampaignInput): CampaignRow[] => {
  if (input.currency === null) return [];
  const selected = new Set(input.channels);
  const cutById = new Map(input.summary.cut.map((r) => [r.id, r]));
  return input.summary.current
    .filter((r) => r.platform !== 'analytics' && isChannelShown(selected, r.platform))
    .filter((r) => input.objective === 'all' || objectiveOf(r) === input.objective)
    .filter((r) => currencyOf(r.id, input) === input.currency)
    .map((r) => ({ r, spend: nullableNumber(r.total_spend) ?? 0 }))
    .filter(({ spend }) => spend > 0)
    .sort((a, b) => b.spend - a.spend || a.r.campaign_name.localeCompare(b.r.campaign_name))
    .slice(0, TOP_CAMPAIGNS)
    .map(({ r, spend }): CampaignRow => ({
      id: r.id,
      name: r.campaign_name,
      platform: r.platform,
      objective: objectiveOf(r),
      spend,
      unclassified: !classifyCampaignName(r.campaign_name).conforms,
      ...resultOf(r, cutById.get(r.id), input),
    }));
};
